// FILE: src/components/admin/LessonProgressTable.jsx
// One student's progress through the chapters or the homelinks, as a table
// (used by the admin compliance record, MTO 11.1.2-3): slides done, a
// labelled progress bar, status as icon + words, completion date, final
// score and credited time. Row actions appear only when not `viewOnly`.
import React from "react";
import { Box, Button, LinearProgress, Stack, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { StatusChip, clampPct } from "../ui";
import { ResponsiveTable } from "./AdminBits";
import { formatDate } from "./format";
import { actionLabel, lessonPercent } from "../student/lessons";
import { formatDuration } from "../../utils/timeFormat";
import { userStatusLabel } from "../../i18n/labels";

const STATUS_STYLE = {
  completed: { tone: "success", Icon: CheckCircleOutlineIcon },
  "in-progress": { tone: "info", Icon: PlayCircleOutlineIcon },
  "not-started": { tone: "default", Icon: RadioButtonUncheckedIcon },
  locked: { tone: "default", Icon: LockOutlinedIcon }
};

/**
 * rows: from mergeLessons() (src/components/student/lessons.js).
 * kind: "chapter" | "homelink". onOpen(row) is called by the row action.
 */
export default function LessonProgressTable({ rows, kind = "chapter", viewOnly = true, blocked, untimed, blockedReasonId, onOpen }) {
  const noun = kind === "homelink" ? "Homelink" : "Chapter";
  const total = rows.reduce((t, r) => t + (r.totalSlides || 0), 0);
  const done = rows.reduce((t, r) => t + Math.min(r.completedSlides || 0, r.totalSlides || 0), 0);
  const overall = total ? clampPct((done / total) * 100) : 0;
  const finished = rows.filter(
    (r) => r.status === "completed" || r.completed || (r.totalSlides > 0 && r.completedSlides >= r.totalSlides)
  ).length;

  return (
    <Box>
      <Box sx={{ mb: 2.5, maxWidth: 560 }}>
        <Typography sx={{ mb: 1 }}>
          <strong>
            {finished} of {rows.length}
          </strong>{" "}
          {kind === "homelink" ? "homelinks" : "chapters"} completed · {done} of {total} slides ({Math.round(overall)}%)
        </Typography>
        <LinearProgress
          variant="determinate"
          value={overall}
          aria-label={`${noun} progress`}
          aria-valuetext={`${done} of ${total} slides, ${Math.round(overall)}%`}
        />
      </Box>

      <ResponsiveTable label={`${noun} progress`} minWidth={viewOnly ? 900 : 1020}>
        <TableHead>
          <TableRow>
            <TableCell component="th" scope="col">
              {noun}
            </TableCell>
            <TableCell component="th" scope="col">
              Slides done
            </TableCell>
            <TableCell component="th" scope="col">
              Progress
            </TableCell>
            <TableCell component="th" scope="col">
              Status
            </TableCell>
            <TableCell component="th" scope="col">
              Completed on
            </TableCell>
            <TableCell component="th" scope="col" align="right">
              Final score
            </TableCell>
            <TableCell component="th" scope="col" align="right">
              Time credited
            </TableCell>
            {!viewOnly && (
              <TableCell component="th" scope="col" align="right">
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const title = row.title || `${noun} ${i + 1}`;
            const pct = Math.round(lessonPercent(row));
            // A finished record is finished, whatever the unlock rule says.
            const status =
              row.completed || (row.totalSlides > 0 && row.completedSlides >= row.totalSlides) ? "completed" : row.status;
            const style = STATUS_STYLE[status] || STATUS_STYLE["not-started"];
            const canOpen = row.unlocked || untimed;
            return (
              <TableRow key={row._id || i}>
                <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                  {title}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {Math.min(row.completedSlides || 0, row.totalSlides || 0)} of {row.totalSlides || 0}
                </TableCell>
                <TableCell sx={{ minWidth: 160 }}>
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      aria-label={`${title} progress`}
                      aria-valuetext={`${pct}%`}
                      sx={{ flex: 1 }}
                    />
                    <Typography component="span" sx={{ minWidth: "3.5ch", textAlign: "right" }}>
                      {pct}%
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <StatusChip tone={style.tone} icon={<style.Icon />} label={userStatusLabel(status)} />
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{status === "completed" ? formatDate(row.completedAt) : "—"}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {row.finalScore > 0 ? `${Math.round(row.finalScore)}%` : "Not submitted"}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {formatDuration(row.timeSpentSec)}
                </TableCell>
                {!viewOnly && (
                  <TableCell align="right">
                    {canOpen ? (
                      <Button
                        variant={row.status === "completed" ? "outlined" : "contained"}
                        disabled={Boolean(blocked)}
                        aria-describedby={blocked ? blockedReasonId : undefined}
                        aria-label={`${actionLabel(row.status)} ${title}`}
                        onClick={() => onOpen && onOpen(row)}
                      >
                        {actionLabel(row.status)}
                      </Button>
                    ) : (
                      <Button variant="outlined" disabled startIcon={<LockOutlinedIcon />} aria-label={`${title} is locked`}>
                        Locked
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </ResponsiveTable>
    </Box>
  );
}
